import React, { useEffect, useRef, useState, useCallback } from 'react';
import useThemeObserver from '../hooks/useThemeObserver';
import './css/JourneyLine.css';

/**
 * JourneyLine — a fixed vertical "trail" on the right edge of the viewport.
 * A line draws itself down the rail as you scroll, with a glowing head dot
 * and a waypoint node per section. Acts as a guide through the single page:
 * shows where you are, what's been covered, and what's next.
 *
 * Also renders a "Next" guide chip (bottom-left) that names the upcoming
 * section and advances on click.
 */

const SECTIONS = [
  { id: 'hero',      num: '000', label: 'Intro' },
  { id: 'platforms', num: '001', label: 'Platforms' },
  { id: 'console',   num: '002', label: 'Console' },
  { id: 'terminal',  num: '003', label: 'Terminal' },
  { id: 'about',     num: '004', label: 'About' },
  { id: 'journey',   num: '005', label: 'Trajectory' },
  { id: 'services',  num: '006', label: 'Services' },
  { id: 'work',      num: '007', label: 'Work' },
  { id: 'contact',   num: '008', label: 'Contact' },
];

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export default function JourneyLine() {
  const theme = useThemeObserver();
  const [progress, setProgress] = useState(0);
  const [fractions, setFractions] = useState(() => SECTIONS.map(() => 0));
  const [activeIdx, setActiveIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(0);

  const compute = useCallback(() => {
    rafRef.current = 0;
    const doc = document.documentElement;
    const maxScroll = doc.scrollHeight - window.innerHeight;
    if (maxScroll <= 0) return;

    const scrollY = window.scrollY;
    const p = clamp01(scrollY / maxScroll);

    // Waypoint fraction: where the rail head sits when this section
    // becomes "active" (its top reaches the probe line under the navbar).
    const probe = 130;
    const fr = SECTIONS.map((s) => {
      const el = document.getElementById(s.id);
      if (!el) return 0;
      const top = scrollY + el.getBoundingClientRect().top;
      return clamp01((top - probe) / maxScroll);
    });

    let active = 0;
    for (let i = 0; i < fr.length; i++) {
      if (p >= fr[i] - 0.002) active = i;
    }

    setProgress(p);
    setFractions(fr);
    setActiveIdx(active);
    setVisible(scrollY > window.innerHeight * 0.25);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Recompute when GSAP ScrollTrigger refreshes (pin changes, resize, etc.)
    const onSTRefresh = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(compute);
    };
    if (window.ScrollTrigger) {
      window.ScrollTrigger.addEventListener('refresh', onSTRefresh);
    }

    // Recompute on DOM mutations (layout shifts, lazy images, etc.)
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        if (!rafRef.current) rafRef.current = requestAnimationFrame(compute);
      });
      document.body && ro.observe(document.body);
    }

    // Short delay for initial layout, then compute immediately
    const t = setTimeout(compute, 100);
    compute();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (window.ScrollTrigger) {
        window.ScrollTrigger.removeEventListener('refresh', onSTRefresh);
      }
      if (ro) ro.disconnect();
      clearTimeout(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [compute]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.4 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };

  const next = SECTIONS[activeIdx + 1] || null;
  const current = SECTIONS[activeIdx];

  return (
    <>
      {/* ── Rail ── */}
      <nav
        className={`jline${visible ? ' jline--visible' : ''}`}
        aria-label="Page journey"
        style={{ '--jl-accent': theme.accent, '--jl-text': theme.text }}
      >
        <div className="jline__track" aria-hidden="true">
          <div className="jline__fill" style={{ height: `${progress * 100}%` }} />
          <div className="jline__head" style={{ top: `${progress * 100}%` }} />
        </div>
        {SECTIONS.map((s, i) => {
          const reached = progress >= fractions[i] - 0.002;
          const isActive = i === activeIdx;
          return (
            <button
              key={s.id}
              type="button"
              className={`jline__wp${reached ? ' jline__wp--reached' : ''}${isActive ? ' jline__wp--active' : ''}`}
              style={{ top: `${fractions[i] * 100}%` }}
              onClick={() => scrollToSection(s.id)}
              aria-label={`Go to ${s.label}`}
              aria-current={isActive ? 'true' : undefined}
            >
              <span className="jline__wp-dot" aria-hidden="true" />
              <span className="jline__wp-tip">
                <span className="jline__wp-num">{s.num}</span>
                <span className="jline__wp-label">{s.label}</span>
              </span>
            </button>
          );
        })}
      </nav>

      {/* ── Next-section guide chip ── */}
      <button
        type="button"
        className={`jguide${visible && next ? ' jguide--visible' : ''}`}
        onClick={() => next && scrollToSection(next.id)}
        style={{ '--jl-accent': theme.accent, '--jl-text': theme.text }}
        aria-label={next ? `Continue to ${next.label}` : 'End of page'}
        tabIndex={visible && next ? 0 : -1}
      >
        <span className="jguide__now">{current.num} — {current.label}</span>
        <span className="jguide__sep" aria-hidden="true" />
        <span className="jguide__next">
          Next&nbsp;·&nbsp;{next ? next.label : ''}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="6" y1="2" x2="6" y2="10" />
            <polyline points="2.5,6.5 6,10 9.5,6.5" />
          </svg>
        </span>
      </button>
    </>
  );
}
